import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, Loader2, CheckCircle2, Save } from 'lucide-react';
import { api } from './api';

const TAGS = [
  { id: 'tantrum',     label: 'Tantrum' },
  { id: 'mood_change', label: 'Mood change' },
  { id: 'sleep_linked',label: 'Sleep-linked' },
  { id: 'regression',  label: 'Regression' },
  { id: 'positive',    label: 'Positive note' },
];
const INTENSITY = [
  { id: 'mild',     label: 'Mild' },
  { id: 'moderate', label: 'Moderate' },
  { id: 'strong',   label: 'Strong' },
];

const Behaviour = () => {
  const navigate = useNavigate();
  const [tag, setTag] = useState('');
  const [intensity, setIntensity] = useState('mild');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [insights, setInsights] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try { setInsights((await api.behaviourInsights()).data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const canSubmit = !!tag;

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await api.postBehaviour({ tag, intensity, notes: notes.trim() || null });
      setNotes('');
      setTag('');
      setIntensity('mild');
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/40 via-white to-white pb-24" data-testid="child-behaviour">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/childrens-health')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Behaviour & emotional health</h1>
              <p className="text-xs text-gray-500 mt-0.5">Log a moment — tantrums, mood shifts, sleep-linked notes.</p>
            </div>
          </div>
        </header>

        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Log a behaviour note</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {TAGS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTag(t.id)}
                className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-2.5 transition-all ${
                  tag === t.id ? 'border-purple-500 bg-purple-50 text-purple-900' : 'border-gray-200 hover:border-purple-300 text-gray-700'
                }`}
                data-testid={`behaviour-tag-${t.id}`}
              >
                {tag === t.id && <CheckCircle2 className="h-4 w-4 inline mr-1.5 text-purple-600" />}
                {t.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {INTENSITY.map((i) => (
              <button
                key={i.id}
                type="button"
                onClick={() => setIntensity(i.id)}
                className={`text-sm font-medium rounded-xl border-2 px-3 py-2 transition-all ${
                  intensity === i.id ? 'border-purple-500 bg-purple-50 text-purple-900' : 'border-gray-200 hover:border-purple-300 text-gray-700'
                }`}
                data-testid={`behaviour-intensity-${i.id}`}
              >
                {i.label}
              </button>
            ))}
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional: short note about what happened…"
            rows={2}
            maxLength={300}
            className="w-full text-sm border-2 border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-purple-400"
            data-testid="behaviour-notes"
          />
          {error && <p className="text-sm text-rose-700 mt-2">{error}</p>}
          <button
            onClick={submit}
            disabled={!canSubmit || submitting}
            className={`w-full mt-3 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm transition-all ${
              canSubmit && !submitting ? 'bg-gradient-to-br from-purple-500 to-violet-500 hover:brightness-110 text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="behaviour-submit"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Save note
          </button>
          {savedFlash && (
            <div className="flex items-center justify-center gap-1.5 mt-2 text-xs text-emerald-700" data-testid="behaviour-saved">
              <Save className="h-3 w-3" /> Saved
            </div>
          )}
        </div>

        {insights && (
          <>
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
              <p className="text-[11px] font-semibold text-purple-700 uppercase tracking-widest mb-2">Suggested adjustments</p>
              <ul className="space-y-2" data-testid="behaviour-focus">
                {insights.focus?.map((f, i) => (
                  <li key={f} className="text-sm text-gray-800 flex items-start gap-2">
                    <span className="text-purple-600 font-bold">{i + 1}.</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Last 14 days · {insights.logged} notes</p>
              <ul className="space-y-2" data-testid="behaviour-observations">
                {insights.observations?.map((o) => (
                  <li key={o} className="text-sm text-gray-700 flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-purple-600 shrink-0 mt-0.5" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 grid grid-cols-3 gap-1 text-center text-[10px] text-gray-500">
                <div><div className="text-base font-bold text-gray-900">{insights.counts?.tantrums || 0}</div>tantrums</div>
                <div><div className="text-base font-bold text-gray-900">{insights.counts?.mood_changes || 0}</div>mood</div>
                <div><div className="text-base font-bold text-gray-900">{insights.counts?.sleep_linked || 0}</div>sleep-linked</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Behaviour;
